import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
  Avatar,
} from '@mui/material';
import {
  DashboardOutlined,
  FolderOutlined,
  HelpOutlineOutlined,
  HubOutlined,
  AssessmentOutlined,
  ScheduleOutlined,
  ExitToAppOutlined,
  AccountCircleOutlined,
  // LanOutlined,
  AdminPanelSettings,
  SettingsApplications,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import Cookies from 'js-cookie';
import { useMemo, useEffect } from 'react';
import useLogout from '../../hooks/useLogout';

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const handleLogout = useLogout();

  const username = Cookies.get('username')?.trim() || 'Guest';
  const dispUserName = username.length > 12 ? username.slice(0, 12) + '...' : username;

  const isActive = (path) => location.pathname === path;

  // --- role check  ---
  const rawRole =
    Cookies.get('Role_id') ??
    Cookies.get('role_id') ;

  const isAdmin = useMemo(() => {
    const n = Number(rawRole);
    return !Number.isNaN(n) && n === 2 || n === 1;
  }, [rawRole]);

  const isSuperAdmin = useMemo(() => {
  const n = Number(rawRole);
  return !Number.isNaN(n) && n === 1;
}, [rawRole]);

  // Get role name for display
  const roleName = useMemo(() => {
    const n = Number(rawRole);
    if (Number.isNaN(n)) return 'User';
    switch (n) {
      case 1:
        return 'Super Admin';
      case 2:
        return 'Admin';
      case 3:
        return 'User';
      default:
        return 'User';
    }
  }, [rawRole]);

  const isUser=useMemo(() => {
    const n = Number(rawRole);
    return !Number.isNaN(n) && n === 3;
  })

  const menu = useMemo(() => {
  const base = [
    { label: 'Dashboard', icon: <DashboardOutlined />, path: '/userdashboard' },
    { label: 'Connections', icon: <HubOutlined />, path: '/myconnections' },
    // { label: 'Connector', icon: <LanOutlined />, path: '/connector' },
    { label: 'My Projects', icon: <FolderOutlined />, path: '/recentworkflows' },
    { label: 'Reports', icon: <AssessmentOutlined />, path: '/reports' },
    { label: 'Schedule Job', icon: <ScheduleOutlined />, path: '/schedule-job' },
  ];

  // Admin vs User specific tab
  if (isAdmin) {
    // 🔹 Decide target based on Super Admin vs Admin
    const adminPath = isSuperAdmin
      ? '/admin/organizations'  
      : '/admin/user';          

    base.push({
      label: 'Admin',
      icon: <AdminPanelSettings />,
      path: adminPath,
    });
  } else if (isUser) {
    base.push({ label: 'Settings', icon: <SettingsApplications />, path: '/settings' });
  }

  // Common Help tab
  base.push({ label: 'Help', icon: <HelpOutlineOutlined />, path: '/help' });

  return base;
}, [isAdmin, isSuperAdmin, isUser]);


  // --- optional guard: if non-admin on admin route, kick to dashboard
  useEffect(() => {
    const onAdminRoute = location.pathname.startsWith('/admin');
    if (onAdminRoute && !isAdmin) {
      navigate('/userdashboard', { replace: true });
    }
  }, [location.pathname, isAdmin, navigate]);

  const handleMenuClick = (path) => navigate(path);

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: 190,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: 190,
          height: '100vh',
          background: '#fff',
          p: 1.25,
          backdropFilter: 'blur(8px)',
          borderRight: '1px solid rgba(255,255,255,0.08)',
          overflowX: 'hidden',
          color: '#fff',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {/* User Info */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'flex-start',
          mt: 3,
          py: 2,
          px: 1,
          borderBottom: '1px solid #E0E0E0',
          mb: 1,
          gap: 1.5,
        }}
      >
        <Avatar sx={{ width: 32, height: 32, bgcolor: '#5A66A6', mt: 0.5 }}>
          <AccountCircleOutlined sx={{ fontSize: '1.2rem' }} />
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{ color: '#3F3F3F', fontWeight: 600, fontSize: '0.875rem' }}
            title={username}
            noWrap
          >
            {dispUserName}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: '#6b7280',
              fontSize: '0.75rem',
              fontWeight: 400,
              display: 'block',
              mt: 0.25,
            }}
          >
            {roleName}
          </Typography>
        </Box>
      </Box>

      {/* Menu */}
      <List sx={{ flex: 1, mt: 1 }}>
        {menu.map(({ label, icon, path }) => {
          const active = isActive(path);
          return (
            <ListItem
              key={label}
              button
              onClick={() => handleMenuClick(path)}
              sx={{
                my: 0.5,
                py: 0.8,
                px: 0.8,
                borderRadius: '4px',
                background: active ? '#5A66A6' : 'transparent',
                '&:hover': {
                  cursor: 'pointer',
                  background: active ? '#5A66A6' : '#EDF4FF',
                  '& .MuiListItemIcon-root, & .MuiTypography-root': {
                    color: active ? '#fff' : '#5A66A6',
                  },
                },
                transition: 'all 0.3s ease-in-out',
              }}
            >
              <ListItemIcon
                sx={{
                  color: active ? '#fff' : '#000',
                  minWidth: 32,
                  mr: 2,
                  justifyContent: 'center',
                }}
              >
                {icon}
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography fontSize="0.875rem" fontWeight={600} color={active ? '#fff' : '#3F3F3F'}>
                    {label}
                  </Typography>
                }
              />
            </ListItem>
          );
        })}
      </List>

      {/* Logout */}
      <Box sx={{ mt: 'auto', borderTop: '1px solid #E0E0E0', pt: 1 }}>
        <ListItem
          button
          onClick={handleLogout}
          sx={{
            py: 0.8,
            px: 0.8,
            borderRadius: '4px',
            '&:hover': {
              cursor: 'pointer',
              background: '#FFE6E6',
              '& .MuiListItemIcon-root, & .MuiTypography-root': { color: '#D32F2F' },
            },
            transition: 'all 0.3s ease-in-out',
          }}
        >
          <ListItemIcon sx={{ color: '#666', minWidth: 32, mr: 2, justifyContent: 'center' }}>
            <ExitToAppOutlined />
          </ListItemIcon>
          <ListItemText
            primary={
              <Typography fontSize="0.875rem" fontWeight={600} color="#3F3F3F">
                Logout
              </Typography>
            }
          />
        </ListItem>
      </Box>
    </Drawer>
  );
};

export default Sidebar;
